import './style.scss'
import { Button, Modal } from 'antd'
import dayjs from 'dayjs'
import { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Api from '@/api/home'
import IconExpried from '@/assets/icon-expried.png'
import IconUsed from '@/assets/icon-used.png'
import { UserStore } from '@/global-states'
import ConvertCouponModal from '../ConvertCouponModal'

interface IProps {
  open: boolean
  toUseCoupon: () => void
  onCancel: () => void
  changeOpen?: (t: boolean) => void
}

const CouponListModal: FC<IProps> = (props) => {
  const { t: translate } = useTranslation(['coupon_modal'])
  const { open, onCancel, toUseCoupon, changeOpen } = props
  const [coupons, setCoupons] = useState<any[]>()
  const [status, setStatus] = useState(-1)
  const [convertCouponModalOpen, setConvertCouponModalOpen] = useState(false)
  const tabs = [
    {
      id: -1,
      name: translate('coupon_modal.all')
    },
    {
      id: 2,
      name: translate('coupon_modal.usable')
    },
    {
      id: 3,
      name: translate('coupon_modal.used')
    },
    {
      id: 4,
      name: translate('coupon_modal.expired')
    }
  ]

  const showCoupons = useMemo(() => {
    if (status === -1) {
      return coupons
    }
    return coupons?.filter((c) => c.status === status)
  }, [coupons, status])

  useEffect(() => {
    if (open) {
      getClaimedCoupons()
    }
  }, [open])

  const getClaimedCoupons = async () => {
    const res = await Api.getClaimedCoupons()
    setCoupons(res.list || [])
  }

  const changeStatus = (status: number) => {
    setStatus(status)
  }

  const onConvertSuccess = () => {
    setConvertCouponModalOpen(false)
    UserStore.updatePayStatus()
    Modal.confirm({
      title: translate('coupon_modal.exchange_success_title'),
      content: <div>{translate('coupon_modal.exchange_success_content')}</div>,
      okText: translate('coupon_modal.use_now'),
      cancelText: translate('coupon_modal.view_now'),
      onOk: () => {
        toUseCoupon()
      },
      onCancel: () => {
        changeOpen?.(true)
      }
    })
  }

  return (
    <>
      <Modal
        maskClosable={true}
        className="title-modal coupon-list-modal"
        onCancel={() => onCancel()}
        footer={null}
        width={700}
        open={open}
        title={translate('coupon_modal.my_coupons')}
      >
        <div className="coupon-tabs">
          {tabs.map((t) => (
            <div
              key={t.id}
              className={`coupon-tabs-item ${status === t.id ? 'actived' : ''}`}
              onClick={changeStatus.bind(this, t.id)}
            >
              <label className={status === t.id ? 'gradient' : ''}>{t.name}</label>
            </div>
          ))}
        </div>

        <div
          className="convert"
          onClick={() => {
            setConvertCouponModalOpen(true)
            onCancel?.()
          }}
        >
          <label className="gradient">{translate('coupon_modal.points_exchange')}</label>
        </div>
        {showCoupons && (
          <div className="coupon-list">
            {showCoupons?.length ? (
              <>
                {showCoupons.map((c) => (
                  <div key={c.id} className={`coupon-list-item ${c.status !== 2 ? 'disabled' : ''}`}>
                    <div className="left">
                      <label className="gradient">{c.deduct_amount / 100}</label>
                      <label className="gradient unit">{translate('coupon_modal.yuan')}</label>
                    </div>
                    <div className="right">
                      <div className="detail">
                        <div className="desc">{c.title}</div>
                        {c.expire_time && (
                          <div className="expried">
                            {translate('coupon_modal.valid_until')}{' '}
                            {dayjs(c.expire_time * 1000).format('YYYY-MM-DD HH:mm')}
                          </div>
                        )}
                        <div className="rules">
                          {c.descriptions?.map((d: string, index: number) => (
                            <div key={index}>{d}</div>
                          ))}
                        </div>
                      </div>
                      {c.status === 2 && (
                        <Button
                          type="primary"
                          onClick={() => {
                            onCancel()
                            toUseCoupon()
                          }}
                        >
                          {translate('coupon_modal.use')}
                        </Button>
                      )}
                    </div>
                    {c.status === 3 && <img className="tag" src={IconUsed} />}
                    {c.status === 4 && <img className="tag" src={IconExpried} />}
                  </div>
                ))}
              </>
            ) : (
              <div className="list-no-data">
                {translate('coupon_modal.no_coupons_message', {
                  status: status !== -1 ? tabs.find((t) => t.id === status)?.name : ''
                })}
              </div>
            )}
          </div>
        )}
      </Modal>

      <ConvertCouponModal
        open={convertCouponModalOpen}
        onCancel={() => setConvertCouponModalOpen(false)}
        onSuccess={onConvertSuccess}
      />
    </>
  )
}

export default CouponListModal
